<template>
  <div v-if="show" class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
    <div class="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 w-full max-w-md border border-amber-700/30 shadow-2xl shadow-amber-900/20 animate-fadeIn">
      <h2 class="text-2xl font-bold mb-4 text-amber-300 flex items-center">
        <svg class="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>
        </svg>
        Changer le mot de passe
      </h2>
      
      <!-- Message d'erreur -->
      <div v-if="error" class="mb-4 p-3 bg-red-900/30 border border-red-700/30 rounded-lg text-red-300">
        {{ error }}
      </div>
      
      <!-- Message de succès -->
      <div v-if="success" class="mb-4 p-3 bg-green-900/30 border border-green-700/30 rounded-lg text-green-300">
        {{ success }}
      </div>
      
      <form @submit.prevent="handleSubmit">
        <div class="mb-4">
          <label class="block text-amber-200 mb-2 font-medium">Mot de passe actuel</label>
          <input 
            type="password" 
            v-model="oldPassword" 
            class="w-full px-4 py-3 bg-gray-900/80 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100"
            :disabled="loading"
            required
          />
        </div>
        
        <div class="mb-4">
          <label class="block text-amber-200 mb-2 font-medium">Nouveau mot de passe</label>
          <input 
            type="password" 
            v-model="newPassword" 
            class="w-full px-4 py-3 bg-gray-900/80 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100"
            :disabled="loading"
            required
          />
        </div>
        
        <div class="mb-6">
          <label class="block text-amber-200 mb-2 font-medium">Confirmer le nouveau mot de passe</label>
          <input 
            type="password" 
            v-model="confirmPassword" 
            class="w-full px-4 py-3 bg-gray-900/80 border border-amber-700/30 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-amber-100"
            :disabled="loading"
            required
          />
          <p v-if="passwordMismatch" class="mt-1 text-red-400 text-sm">
            Les mots de passe ne correspondent pas
          </p>
        </div>
        
        <div class="flex justify-end space-x-3">
          <button 
            type="button" 
            @click="close"
            class="px-5 py-2.5 border border-amber-700/30 text-amber-300 rounded-lg hover:bg-amber-900/20 transition-all duration-300"
            :disabled="loading"
          >
            Annuler
          </button>
          <button 
            type="submit"
            class="bg-gradient-to-r from-amber-500 to-amber-600 text-black px-5 py-2.5 rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-300 font-medium shadow-lg shadow-amber-900/20 flex items-center"
            :disabled="loading || passwordMismatch"
          >
            <svg v-if="loading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Changer le mot de passe
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import UserService from '../services/user.service';

export default {
  name: 'PasswordChangeModal',
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const oldPassword = ref('');
    const newPassword = ref('');
    const confirmPassword = ref('');
    const error = ref('');
    const success = ref('');
    const loading = ref(false);
    
    // Computed
    const passwordMismatch = computed(() => 
      newPassword.value && confirmPassword.value && newPassword.value !== confirmPassword.value
    );
    
    // Methods
    const handleSubmit = async () => {
      if (passwordMismatch.value) {
        return;
      }
      
      loading.value = true;
      error.value = '';
      success.value = '';
      
      try {
        // Utiliser directement le service sans passer par le store
        // pour simplifier le processus de débogage
        await UserService.changePassword(oldPassword.value, newPassword.value);
        
        success.value = "Mot de passe changé avec succès";
        // Réinitialiser les champs après un certain délai
        setTimeout(() => {
          oldPassword.value = '';
          newPassword.value = '';
          confirmPassword.value = '';
          // Fermer la modal après un court délai
          setTimeout(() => {
            emit('close');
          }, 1500);
        }, 500);
      } catch (err) {
        console.error("Erreur de changement de mot de passe:", err);
        if (err.response) {
          console.error("Détails de la réponse:", {
            status: err.response.status,
            data: err.response.data,
            headers: err.response.headers
          });
          error.value = `Erreur ${err.response.status}: ${err.response.data?.message || JSON.stringify(err.response.data) || 'Erreur inconnue'}`;
        } else if (err.request) {
          console.error("Détails de la requête:", err.request);
          error.value = "Le serveur n'a pas répondu à la requête";
        } else {
          error.value = err.message || "Une erreur inconnue est survenue";
        }
      } finally {
        loading.value = false;
      }
    };
    
    const close = () => {
      emit('close');
    };
    
    // Nettoyage lorsque la modal se ferme
    watch(() => props.show, (newVal) => {
      if (!newVal) {
        oldPassword.value = '';
        newPassword.value = '';
        confirmPassword.value = '';
        error.value = '';
        success.value = '';
      }
    });
    
    return {
      oldPassword,
      newPassword,
      confirmPassword,
      loading,
      error,
      success,
      passwordMismatch,
      handleSubmit,
      close
    };
  }
};
</script>

<style scoped>
@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-out forwards;
}
</style> 